// src/api/apiClient.js

import axios from 'axios';

// --- Configuration ---

// Define the base URL for your backend API routes (the ones under /api)
// Use an environment variable for flexibility between development/production
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Key used to store the authentication token in localStorage
// Ensure this matches the key used in your AuthContext/login logic
const AUTH_TOKEN_KEY = 'token'; // Or 'authToken' if that's what you used in AuthContext

// --- Create Axios Instance ---

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    // REMOVED 'Content-Type': 'application/json',
    // Axios will now automatically set the correct Content-Type.
    // You can add other default headers here if needed
    // 'Accept': 'application/json', // Example
  },
  // You can also configure timeout here (e.g., timeout: 10000 // 10 seconds)
});

// --- Interceptors ---

// Request Interceptor: Runs before each request is sent
apiClient.interceptors.request.use(
  (config) => {
    // Get the token from localStorage
    const token = localStorage.getItem(AUTH_TOKEN_KEY);

    // If a token exists, add it to the Authorization header
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
      console.log('[API Client Request] Token attached:', !!token); // Log if token attached
    } else {
        console.log('[API Client Request] No token found in localStorage.');
    }

    // Important: Check if data is FormData. If so, DO NOT set Content-Type manually
    // Allowing Axios to set it automatically handles the boundary correctly.
    // This check might be redundant now that the default is removed, but good for safety.
    if (config.data instanceof FormData) {
        // Let Axios handle the Content-Type for FormData
        delete config.headers['Content-Type']; // Ensure no manual override
        console.log('[API Client Request] FormData detected, letting Axios set Content-Type.');
    } else {
        // For regular JSON data etc., you could set a default Content-Type here if needed,
        // but often it's better to let Axios decide based on the data type.
        // config.headers['Content-Type'] = 'application/json'; // Example if needed ONLY for non-FormData
    }


    // Must return the config object, otherwise the request won't proceed
    return config;
  },
  (error) => {
    // Handle request configuration errors (unlikely in basic setup)
    console.error('[API Client Request Error]', error);
    return Promise.reject(error);
  }
);

// Response Interceptor: Runs when a response is received
apiClient.interceptors.response.use(
  (response) => {
    // Any status code within the range of 2xx causes this function to trigger
    // Simply return the successful response
    console.log('[API Client Response Success]', response.status, response.config.url);
    return response;
  },
  (error) => {
    // Any status codes outside the range of 2xx causes this function to trigger
    console.error('[API Client Response Error]', error.response?.status, error.config?.url, error.message);

    // Check if the error is specifically a 401 (Unauthorized) or 403 (Forbidden)
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      console.warn(`[API Client] Received ${error.response.status} status. Token may be invalid or expired.`);

      // Token is likely invalid/expired. Perform cleanup:
      // 1. Remove the token from localStorage
      localStorage.removeItem(AUTH_TOKEN_KEY);
      // 2. Remove any related user info stored locally (optional, depends on your AuthContext setup)
      localStorage.removeItem('userInfo'); // Example: Adjust if you use a different key

      // 3. Redirect the user to the login page
      // Check if already on login page to prevent redirect loop
      if (!window.location.pathname.includes('/login')) { // Adjust '/login' if your path differs
        console.log('[API Client] Redirecting to login page due to 401/403 error.');
        // Use window.location.replace for a hard redirect (clears history)
        window.location.replace('/login'); // Adjust path if needed
        // Alternatively, if using react-router, you might use history.push('/login')
        // but accessing history outside components can be tricky. window.location is simpler here.
      } else {
        console.log('[API Client] Already on login page, skipping redirect.');
      }

      // Important: Reject the promise so the original caller's .catch() block still runs
      // This allows components to potentially display a specific message before the redirect happens
      // or handle the error if they are *expecting* a possible 401/403 (e.g., on initial load).
      // We modify the error slightly to indicate it was handled by the interceptor.
      error.handledByInterceptor = true;
      return Promise.reject(error);

    } else {
      // For other errors (e.g., 404 Not Found, 500 Server Error, network errors),
      // just let the error propagate to the calling code's .catch() block.
      return Promise.reject(error);
    }
  }
);

// --- Export Instance ---

export default apiClient;